import type { CollabDeliveryMode } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { DeliveryBanner } from '../outreach/delivery-banner';
import { isReachable } from '../outreach/collab-recipient-helpers';
import type { CollabWizardRecipient } from './use-collab-recipients';
import { recipientNameFor } from './collab-wizard-helpers';

const DELIVERY_K = 'invitations.modal.collab.wizard.delivery';
const PICKER_K = 'invitations.modal.collab.wizard.send.channelPicker';

interface CollabDeliveryBannerProps {
  mode: CollabDeliveryMode;
  recipients: CollabWizardRecipient[];
  /** Back to the channel radios; the composed body survives in the store. */
  onChangeChannel: () => void;
}

/** Banner title per mode: email has its own; link and DM reuse the radio-row
 * titles so the banner echoes the choice just made. */
const TITLE_KEY: Record<CollabDeliveryMode, string> = {
  email: `${DELIVERY_K}.bannerTitleEmail`,
  link: `${PICKER_K}.link.title`,
  dm: `${PICKER_K}.dm.title`,
};

/**
 * Recipient banner heading the sponsored delivery step: a thin wrapper over the
 * shared {@link DeliveryBanner} that computes the mode title and the
 * mode-specific subline from the wizard recipients.
 */
export const CollabDeliveryBanner = ({
  mode,
  recipients,
  onChangeChannel,
}: CollabDeliveryBannerProps) => {
  const { t } = useTranslation();
  const mono = recipients.length === 1 ? recipients[0] : null;

  const subline = (() => {
    if (mode === 'email') {
      const emailCount = recipients.filter((r) => isReachable(r) && !!r.email).length;
      return mono
        ? t(`${DELIVERY_K}.sentTo`, { email: mono.email ?? '' })
        : t(`${DELIVERY_K}.sentToBulk`, { count: emailCount });
    }
    if (mode === 'dm') {
      return t(`${DELIVERY_K}.dmTo`, { name: recipientNameFor(mono, t) });
    }
    return mono
      ? t(`${DELIVERY_K}.handLinkTo`, { name: recipientNameFor(mono, t) })
      : t(`${DELIVERY_K}.handLinkToBulk`, { count: recipients.filter(isReachable).length });
  })();

  return (
    <DeliveryBanner
      avatar={mono ? { name: recipientNameFor(mono, t), src: mono.avatarUrl } : null}
      count={recipients.length}
      title={t(TITLE_KEY[mode])}
      subline={subline}
      changeLabel={t(`${DELIVERY_K}.change`)}
      onChange={onChangeChannel}
    />
  );
};
